import type { Sprite } from 'pixi.js';
import type { HalfTimeEffect } from '../../data/full/babacool';
import { NOTE } from '../../sim/full/attacks/echo';
import { HALF_TIME, halfTimeReach } from '../../sim/full/skills/half-time';
import type { PlayerState, PropState, SimEvent, SimState } from '../../sim/state';
import { createBurster } from '../class-bursts';
import type { Family, RenderContext } from '../context';
import type { Frame } from '../frame';
import { type Sheet, TAU, paintSheet, paintedLater } from '../paint';
import { frac, lerp } from '../../shared/scalar';
import { add, byId, hide, lookup, setTint } from '../util';
import { ViewPool } from '../views';
import { DUB_PIECES } from './babacool-paint';

const NOTES = {
  extent: [24, 20, 17],
  alpha: [1, 0.85, 0.7],
  glow: 2.4,
  glowAlpha: [0.6, 0.4, 0.3],
  sway: 0.18,
  swayBeats: 2,
} as const;
const ECHO_CREAM = 0xfff6dc;
const SET_OFF = { beats: 0.4, from: 6, to: 26, peak: 0.7 } as const;
const HALF = {
  fade: 0.3,
  floor: 0.22,
  dayFloor: 0.14,
  dashes: 0.8,
  turnBeats: 32,
  pulseBeats: 2,
  pulseFrom: 0.2,
  pulseAlpha: 0.55,
  open: { beats: 0.5, peak: 0.8 },
} as const;

interface NoteView {
  readonly glow: Sprite;
  readonly body: Sprite;
}

interface HalfView {
  readonly floor: Sprite;
  readonly dashes: Sprite;
  readonly pulse: Sprite;
}

export function createDub(ctx: RenderContext): Family {
  const { layers, textures } = ctx;
  const players = new Set<string>();
  for (const [classId, attack] of ctx.attacks) {
    if (attack.kind === 'echo') {
      players.add(classId);
    }
  }
  const halfTimes = new Map<string, HalfTimeEffect>();
  for (const [classId, effect] of ctx.skillEffects) {
    if (effect.kind === 'halfTime') {
      halfTimes.set(classId, effect);
    }
  }
  const sheet = paintedLater((): Sheet<keyof typeof DUB_PIECES> => paintSheet(DUB_PIECES));
  const burster = createBurster(ctx, 48);
  const seen = new Map<number, number>();
  const born = new Map<number, number>();
  let notes: ViewPool<NoteView> | undefined;
  let halves: ViewPool<HalfView> | undefined;
  let image = 0;

  function reachOf(owner: PlayerState): number {
    const effect = halfTimes.get(owner.classId);
    return effect === undefined ? 0 : halfTimeReach(owner, effect);
  }

  function colorOf(owner: PlayerState, frame: Frame): number {
    return frame.palette[lookup(ctx.classTokens, owner.classId, 'class')];
  }

  function beatsOf(frame: Frame): number {
    return (frame.now - frame.tempo.originTick) / frame.tempo.ticksPerBeat;
  }

  function drawNote(prop: PropState, owner: PlayerState, alpha: number, frame: Frame): void {
    const shapes = sheet.get().shapes;
    notes ??= new ViewPool<NoteView>(
      () => ({ glow: add(layers.glow, textures.halo), body: add(layers.shots, shapes.note) }),
      (view) => {
        hide(view.glow, view.body);
      },
    );
    const rank = Math.min(prop.rank ?? 0, NOTES.extent.length - 1);
    const x = lerp(prop.prevX, prop.x, alpha);
    const y = lerp(prop.prevY, prop.y, alpha);
    const color = colorOf(owner, frame);
    const tint = rank > 0 && frame.light.additive ? ECHO_CREAM : color;
    const extent = NOTES.extent[rank] ?? NOTES.extent[0];
    const view = notes.acquire(prop.id);
    view.body.visible = view.glow.visible = true;
    view.body.position.set(x, y);
    view.body.scale.set(extent / shapes.note.radius);
    view.body.rotation = frame.calm
      ? 0
      : NOTES.sway * Math.sin((TAU * beatsOf(frame)) / NOTES.swayBeats + prop.id);
    view.body.alpha = NOTES.alpha[rank] ?? 1;
    setTint(view.body, tint);
    view.glow.position.set(x, y);
    view.glow.scale.set((extent * NOTES.glow) / textures.halo.radius);
    view.glow.alpha = (NOTES.glowAlpha[rank] ?? 0.3) * frame.light.haloAlpha;
    setTint(view.glow, color);
    if ((prop.rank ?? 0) > 0 && !seen.has(prop.id)) {
      burster.spawn(frame, {
        shape: textures.ring,
        now: frame.now,
        duration: SET_OFF.beats * frame.tempo.ticksPerBeat,
        x: prop.prevX,
        y: prop.prevY,
        fromRadius: SET_OFF.from,
        toRadius: SET_OFF.to,
        tint,
        peak: SET_OFF.peak,
      });
    }
    seen.set(prop.id, image);
  }

  function drawHalf(prop: PropState, owner: PlayerState, alpha: number, frame: Frame): void {
    const shapes = sheet.get().shapes;
    halves ??= new ViewPool<HalfView>(
      () => ({
        floor: add(layers.zones, shapes.floor),
        dashes: add(layers.fx, shapes.dashes),
        pulse: add(layers.fx, textures.ring),
      }),
      (view) => {
        hide(view.floor, view.dashes, view.pulse);
      },
    );
    const { now, tempo, calm, light } = frame;
    if (!born.has(prop.id)) {
      born.set(prop.id, now);
    }
    const age = (now - (born.get(prop.id) ?? now)) / tempo.ticksPerBeat;
    const left = (prop.ticksLeft - alpha) / tempo.ticksPerBeat;
    const shown = Math.max(0, Math.min(1, age / HALF.fade, left / HALF.fade));
    const reach = reachOf(owner);
    const x = lerp(owner.prevX, owner.x, alpha);
    const y = lerp(owner.prevY, owner.y, alpha);
    const color = colorOf(owner, frame);
    const beats = beatsOf(frame);
    const view = halves.acquire(prop.id);
    view.floor.visible = view.dashes.visible = true;
    view.floor.position.set(x, y);
    view.floor.scale.set(reach / shapes.floor.radius);
    view.floor.alpha = (light.additive ? HALF.floor : HALF.dayFloor) * shown;
    setTint(view.floor, color);
    view.dashes.position.set(x, y);
    view.dashes.scale.set(reach / shapes.dashes.radius);
    view.dashes.rotation = calm ? 0 : (TAU * beats) / HALF.turnBeats;
    view.dashes.alpha = HALF.dashes * shown;
    setTint(view.dashes, color);
    view.pulse.visible = !calm;
    if (!calm) {
      const share = frac(beats / HALF.pulseBeats);
      view.pulse.position.set(x, y);
      view.pulse.scale.set(
        (reach * (HALF.pulseFrom + (1 - HALF.pulseFrom) * share)) / textures.ring.radius,
      );
      view.pulse.alpha = HALF.pulseAlpha * (1 - share) * shown;
      setTint(view.pulse, color);
    }
    seen.set(prop.id, image);
  }

  function opened(event: SimEvent, state: SimState, frame: Frame): void {
    if (event.type !== 'skillUsed') {
      return;
    }
    const player = byId(state.players, event.playerId);
    if (player === undefined || !players.has(player.classId)) {
      return;
    }
    const reach = reachOf(player);
    burster.spawn(frame, {
      shape: textures.ring,
      now: frame.now,
      duration: HALF.open.beats * frame.tempo.ticksPerBeat,
      x: 0,
      y: 0,
      fromRadius: player.radius,
      toRadius: reach,
      tint: colorOf(player, frame),
      peak: HALF.open.peak,
      anchor: 'player',
      anchorId: player.id,
    });
  }

  return {
    reset(): void {
      seen.clear();
      born.clear();
      burster.bursts.clear();
      notes?.releaseAll();
      halves?.releaseAll();
    },
    onEvent: opened,
    update(state: SimState, alpha: number, frame: Frame): void {
      if (players.size === 0) {
        return;
      }
      image += 1;
      notes?.begin();
      halves?.begin();
      for (const prop of state.props ?? []) {
        if (prop.kind !== NOTE && prop.kind !== HALF_TIME) {
          continue;
        }
        const owner = byId(state.players, prop.playerId);
        if (owner === undefined) {
          continue;
        }
        if (prop.kind === HALF_TIME) {
          drawHalf(prop, owner, alpha, frame);
        } else if (prop.phase === 'flying') {
          drawNote(prop, owner, alpha, frame);
        }
      }
      notes?.end();
      halves?.end();
      for (const [id, last] of seen) {
        if (last !== image) {
          seen.delete(id);
          born.delete(id);
        }
      }
      burster.bursts.update(state, alpha, frame);
    },
    destroy(): void {
      burster.bursts.clear();
    },
  };
}
